import type { track as TrackFn } from "pro-features/components/WithTrack/src/context";
import { SearchType } from "app/pages/sales-intelligence/services/tracking/home-page/types";
import createTrackingEventName from "app/pages/sales-intelligence/utils/testing/createTrackingEventName";

import createCardsTrackingService from "./cards/factory";
import createTableTrackingService from "./table/factory";

const createHomePageTrackingService = (track: TrackFn) => {
    const trackSearchBarEvent = (action: string, eventName: string) => {
        track("search bar", action, eventName);
    };

    const trackTargetAccountsAction = (eventName: string) => {
        track("Win target accounts", "click", eventName);
    };

    const trackRecentResultEvent = (action: string, eventName: string) => {
        track("recent results", action, eventName);
    };

    const trackSuggestionEvent = (action: string, eventName: string) => {
        track("suggested companies", action, eventName);
    };

    const trackWidgets = (action: string, eventName: string, eventSubName: string) => {
        track("home page widgets", action, createTrackingEventName(eventName, eventSubName));
    };

    const trackTopCompaniesWidget = (action: string, eventName: string) => {
        trackWidgets(action, eventName, "top companies");
    };

    const trackRecentDomainClicked = (domain: string, isSuggestion?: boolean) => {
        const eventName = createTrackingEventName("analyze account", "website", domain);

        if (isSuggestion) {
            trackSuggestionEvent("select suggestion", eventName);
        } else {
            trackRecentResultEvent("select recent result", eventName);
        }
    };

    const trackRecentCompanyClicked = (companyName: string, isSuggestion?: boolean) => {
        const eventName = createTrackingEventName("analyze account", "company", companyName);

        if (isSuggestion) {
            trackSuggestionEvent("select suggestion", eventName);
        } else {
            trackRecentResultEvent("select recent result", eventName);
        }
    };

    return {
        cards: createCardsTrackingService(track),

        table: createTableTrackingService(track),

        trackDomainClicked(searchType: SearchType, domain: string) {
            const eventName = createTrackingEventName("search website", searchType, domain);

            trackSearchBarEvent("click search website", eventName);
        },
        trackDomainHovered() {
            trackSearchBarEvent("hover search website", "search website hover");
        },
        trackRecentDomainClicked,
        trackRecentCompanyClicked,

        trackCompanyClicked(companyName: string) {
            const eventName = createTrackingEventName("search company", SearchType.New, companyName);

            trackSearchBarEvent("click search company", eventName);
        },
        trackCompanyExpand(websitesCount: number, companyName: string) {
            const eventName = createTrackingEventName("view company websites", websitesCount, companyName);

            trackSearchBarEvent("expand company", eventName);
        },
        trackCompanyCollapse(websitesCount: number, companyName: string) {
            const eventName = createTrackingEventName("close company websites", websitesCount, companyName);

            trackSearchBarEvent("collapse company", eventName);
        },

        trackStaticListClicked(numberOfDomains: number) {
            trackTargetAccountsAction(`existing list/static list/${numberOfDomains}`);
        },
        trackSavedSearchClicked(numberOfResults: number) {
            trackTargetAccountsAction(`existing list/saved search/${numberOfResults}`);
        },
        trackRevealContact(): void {
            trackTopCompaniesWidget("click reveal", "reveal contact");
        },
        trackCraftEmail(): void {
            trackTopCompaniesWidget("click", "craft an email");
        },
        trackViewCompanyAnalysis(): void {
            trackTopCompaniesWidget("click", "view company analysis");
        },
    };
};

export default createHomePageTrackingService;
